import React, { useCallback, useMemo } from "react";
import { X } from "lucide-react";

import CommentInput from "components/Comments/CommentInput";
import ContextCommentPicker from "components/Comments/ContextCommentPicker";
import { DateTimePickerInput } from "ui/DateTimePicker";
import type { Comment } from "util/constants";
import type { PrivacyLevel } from "util/userUtils";
import type { ContextCommentKind } from "lib/api/contextComments";
import { getTaskUiStrings, resolveTaskLocale } from "i18n/taskUi";

/**
 * Discriminated draft shape: either a fresh comment the user is writing
 * inline ("new") or an existing owner-anchored comment the user picked
 * via `ContextCommentPicker` to attach ("existing"). Both shapes carry
 * an optional due date (`dueAt`) used by task drafts and ignored by
 * principle drafts.
 */
export type ContextCommentDraft =
  | {
      localId: string;
      source: "new";
      /** TipTap HTML; sent as `content` to the attach endpoint. */
      content: string;
      dueAt: Date | null;
    }
  | {
      localId: string;
      source: "existing";
      /** Id of the caller's existing user-owned comment. */
      existingCommentId: number;
      /** Cached preview -- shown as read-only next to the date picker. */
      existingPreview: string;
      dueAt: Date | null;
    };

export interface ContextCommentDraftListProps {
  kind: ContextCommentKind;
  drafts: ContextCommentDraft[];
  onDraftsChange: (next: ContextCommentDraft[]) => void;
  /**
   * Parent post's privacy. Drives three things:
   *   - `forcedPrivacy` on each inline `CommentInput` so new drafts
   *     inherit the host's audience.
   *   - Which audiences are offered by the picker (anonymous hosts
   *     only see public/anonymous rows so personal/private rows are
   *     never leaked via attachment).
   *   - Owner-scope: non-anonymous hosts still prioritize the user's
   *     own rows; anonymous hosts go straight to the global pool.
   */
  parentPrivacy: PrivacyLevel;
  /** Show the due-date picker per row (true for tasks, false for principles). */
  withDueDate?: boolean;
  isMobile?: boolean;
  title?: string;
  subtitle?: string;
  addButtonLabel?: string;
  emptyLabel?: string;
  /**
   * When `false`, skip rendering the outer card container + title/
   * subtitle header. Useful when the surrounding form already owns the
   * "Tasks for this action" / "Principles for this solution" chrome.
   * Defaults to `true` for backward compatibility.
   */
  renderChrome?: boolean;
}

const createLocalId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `ccd-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const htmlToPlainText = (html: string): string =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Renders a managed list of "draft" context comments (principles or
 * tasks) that will be attached to a post when the surrounding form is
 * submitted. Supports both inline-new drafts and picker-sourced
 * existing comments, plus an optional due-date picker for tasks.
 */
const ContextCommentDraftList: React.FC<ContextCommentDraftListProps> = ({
  kind,
  drafts,
  onDraftsChange,
  parentPrivacy,
  withDueDate = false,
  isMobile = false,
  title,
  subtitle,
  addButtonLabel,
  emptyLabel,
  renderChrome = true,
}) => {
  const labels = useMemo(() => getTaskUiStrings(resolveTaskLocale()), []);
  const effectiveTitle = title || (kind === "principle" ? labels.attachedPrinciplesTitle : labels.attachedTasksTitle);
  const effectiveSubtitle = subtitle || (kind === "principle" ? labels.attachedPrinciplesSubtitle : labels.attachedTasksSubtitle);
  const effectiveAddLabel = addButtonLabel || (kind === "principle" ? labels.pickerCreateNewPrinciple : labels.pickerCreateNewTask);
  const effectiveEmptyLabel = emptyLabel || (kind === "principle" ? labels.attachedPrinciplesEmpty : labels.attachedTasksEmpty);
  const removeAriaLabel = kind === "principle" ? labels.removePrinciple : labels.removeTask;

  // Anonymous hosts must only be able to attach publicly-reusable rows
  // (public or anonymous privacy). They also should not show the
  // user's own private rows since those would leak identity if picked.
  // Non-anonymous hosts prioritize the user's own rows then fall back
  // to the global pool with no audience restriction.
  const isAnonymousHost = parentPrivacy === "anonymous";
  const pickerOwnerScope = isAnonymousHost ? "global" : "mixed";
  const pickerAudiences = isAnonymousHost ? ["public", "anonymous"] : undefined;
  // Anonymous drafts are stored as anonymous; forcing privacy to
  // `anonymous` on the composer keeps the UI signal consistent. For
  // all other hosts, the draft inherits the host's privacy as-is.
  const draftForcedPrivacy: PrivacyLevel = parentPrivacy;

  const excludedIds = useMemo(
    () =>
      drafts
        .filter((draft): draft is Extract<ContextCommentDraft, { source: "existing" }> =>
          draft.source === "existing"
        )
        .map((draft) => draft.existingCommentId),
    [drafts]
  );

  const handlePick = useCallback(
    (comment: Comment) => {
      const existingCommentId = Number(comment.id);
      if (!Number.isFinite(existingCommentId) || existingCommentId <= 0) {
        return;
      }
      const preview = htmlToPlainText(comment.content || "");
      onDraftsChange([
        ...drafts,
        {
          localId: createLocalId(),
          source: "existing",
          existingCommentId,
          existingPreview: preview,
          dueAt: null,
        },
      ]);
    },
    [drafts, onDraftsChange]
  );

  const handleCreateNew = useCallback(
    (seed: { content: string }) => {
      const seededHtml = seed.content.trim().length > 0 ? `<p>${seed.content.trim()}</p>` : "";
      onDraftsChange([
        ...drafts,
        {
          localId: createLocalId(),
          source: "new",
          content: seededHtml,
          dueAt: null,
        },
      ]);
    },
    [drafts, onDraftsChange]
  );

  const updateDraft = useCallback(
    (localId: string, patch: Partial<ContextCommentDraft>) => {
      onDraftsChange(
        drafts.map((draft) =>
          draft.localId === localId ? ({ ...draft, ...patch } as ContextCommentDraft) : draft
        )
      );
    },
    [drafts, onDraftsChange]
  );

  const removeDraft = useCallback(
    (localId: string) => {
      onDraftsChange(drafts.filter((draft) => draft.localId !== localId));
    },
    [drafts, onDraftsChange]
  );

  const body = (
    <>
      <ContextCommentPicker
        kind={kind}
        ownerScope={pickerOwnerScope}
        audiences={pickerAudiences}
        onPick={handlePick}
        onCreateNew={handleCreateNew}
        excludeCommentIds={excludedIds}
      />

      {drafts.length === 0 ? (
        <div className="rounded-md border border-dashed border-eoe-border-main px-3 py-3 text-xs text-eoe-text-sub dark:border-eoe-dark-border-main dark:text-eoe-dark-text-sub">
          {effectiveEmptyLabel}
        </div>
      ) : null}

      {drafts.map((draft) => (
        <div
          key={draft.localId}
          className="relative flex flex-col gap-2 rounded-md border border-eoe-border-main p-3 pr-10 dark:border-eoe-dark-border-main"
        >
          <button
            type="button"
            onClick={() => removeDraft(draft.localId)}
            className="absolute right-2 top-2 rounded-full p-1 text-warning-red transition-colors hover:bg-warning-red/10 hover:text-dark-warning-red focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-warning-red dark:text-dark-warning-red"
            aria-label={removeAriaLabel}
            title={removeAriaLabel}
          >
            <X className="size-4" aria-hidden />
          </button>

          {draft.source === "existing" ? (
            <div className="text-sm italic text-eoe-text dark:text-eoe-dark-text">
              {draft.existingPreview || effectiveAddLabel}
            </div>
          ) : (
            <CommentInput
              action="form"
              buttonText=""
              initialContent={draft.content}
              onSubmit={() => {
                /*
                 * Submission is owned by the parent post form; the
                 * inline composer's submit is never used directly. The
                 * parent serializes each draft's HTML and POSTs it to
                 * the attach endpoint.
                 */
              }}
              onContentChange={(html) => updateDraft(draft.localId, { content: html })}
              onCoauthorsChange={() => undefined}
              forcedPrivacy={draftForcedPrivacy}
              showPrivacy={false}
              showTimeline={false}
              showPlace={false}
              allowCoauthors={false}
              hideFooter
              placeholder={
                kind === "principle"
                  ? labels.pickerCreateNewPrinciple
                  : labels.taskTitlePlaceholder
              }
              onCanSubmitChange={() => undefined}
              onMediaSelected={() => undefined}
              onMediaManagerChange={() => undefined}
              isMobile={isMobile}
            />
          )}

          {withDueDate ? (
            <div className="flex flex-wrap items-center gap-2">
              <DateTimePickerInput
                value={draft.dueAt}
                onChange={(date) => updateDraft(draft.localId, { dueAt: date })}
                isMobile={isMobile}
                placeholder={labels.taskDueLabel}
              />
            </div>
          ) : null}
        </div>
      ))}
    </>
  );

  if (!renderChrome) {
    return <div className="space-y-3">{body}</div>;
  }

  return (
    <div className="form-control space-y-3 rounded-lg border border-eoe-border-main p-3 dark:border-eoe-dark-border-main">
      <div>
        <div className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
          {effectiveTitle}
        </div>
        <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
          {effectiveSubtitle}
        </p>
      </div>
      {body}
    </div>
  );
};

export default ContextCommentDraftList;
